import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Search, Pencil, Loader2, Filter } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";

const PVC_LABEL = { has_pvc: "Has PVC", needs_transfer: "Needs Transfer", registering_new: "New Reg" };

export default function Members() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [members, setMembers] = useState([]);
  const [lgas, setLgas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [fLga, setFLga] = useState("all");
  const [fPvc, setFPvc] = useState("all");
  const [fReward, setFReward] = useState("all");
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/structure/lgas").then((r) => setLgas(r.data.lgas)).catch(() => {}); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (fLga !== "all") params.lga = fLga;
      if (fPvc !== "all") params.pvc_status = fPvc;
      if (fReward !== "all") params.reward_status = fReward;
      const { data } = await api.get("/admin/members", { params });
      setMembers(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [search, fLga, fPvc, fReward]);

  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/members/${editing.member_id}`, {
        full_name: editing.full_name, phone: editing.phone, ward: editing.ward,
        polling_unit: editing.polling_unit, pvc_status: editing.pvc_status,
      });
      toast.success("Member updated");
      setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="font-heading font-black text-3xl">Member Directory</h1>
        <p className="text-muted-foreground">{isSuper ? "All members statewide." : `Members in ${user?.assigned_lga} LGA only.`}</p>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 mb-5">
        <div className="grid md:grid-cols-4 gap-3">
          <div className="relative md:col-span-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input data-testid="member-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Name, phone, ID..." className="pl-9" />
          </div>
          {isSuper && (
            <Select value={fLga} onValueChange={setFLga}>
              <SelectTrigger data-testid="filter-lga"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All LGAs</SelectItem>{lgas.map((l) => <SelectItem key={l.code} value={l.name}>{l.name}</SelectItem>)}</SelectContent>
            </Select>
          )}
          <Select value={fPvc} onValueChange={setFPvc}>
            <SelectTrigger data-testid="filter-pvc"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All PVC</SelectItem><SelectItem value="has_pvc">Has PVC</SelectItem><SelectItem value="needs_transfer">Needs Transfer</SelectItem><SelectItem value="registering_new">New Reg</SelectItem></SelectContent>
          </Select>
          <Select value={fReward} onValueChange={setFReward}>
            <SelectTrigger data-testid="filter-reward"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Rewards</SelectItem><SelectItem value="pending">Pending</SelectItem><SelectItem value="disbursed">Disbursed</SelectItem></SelectContent>
          </Select>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-3">Member ID</th><th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Phone</th><th className="px-4 py-3">LGA / Ward</th>
                <th className="px-4 py-3">PVC</th><th className="px-4 py-3">Reward</th><th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="px-4 py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></td></tr>
              ) : members.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-16 text-center text-muted-foreground"><Filter className="w-8 h-8 mx-auto mb-2 opacity-40" />No members found.</td></tr>
              ) : members.map((m) => (
                <tr key={m.member_id} data-testid={`member-row-${m.member_id}`} className="border-t border-slate-50 hover:bg-slate-50/50">
                  <td className="px-4 py-3 font-mono text-xs">{m.member_id}</td>
                  <td className="px-4 py-3 font-semibold">{m.full_name}</td>
                  <td className="px-4 py-3">{m.phone}</td>
                  <td className="px-4 py-3"><div>{m.lga}</div><div className="text-xs text-muted-foreground">{m.ward}</div></td>
                  <td className="px-4 py-3"><Badge variant="secondary" className="font-normal">{PVC_LABEL[m.pvc_status] || m.pvc_status}</Badge></td>
                  <td className="px-4 py-3">{m.reward_status === "disbursed" ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Disbursed</Badge> : <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">Pending</Badge>}</td>
                  <td className="px-4 py-3"><Button data-testid={`edit-${m.member_id}`} size="icon" variant="ghost" onClick={() => setEditing({ ...m })}><Pencil className="w-4 h-4" /></Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!loading && members.length > 0 && <div className="px-4 py-3 text-xs text-muted-foreground border-t">{members.length} member(s)</div>}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit Member — {editing?.member_id}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div><Label className="mb-1.5 block text-sm font-semibold">Full Name</Label><Input data-testid="edit-name" value={editing.full_name} onChange={(e) => setEditing({ ...editing, full_name: e.target.value })} /></div>
              <div><Label className="mb-1.5 block text-sm font-semibold">Phone</Label><Input data-testid="edit-phone" value={editing.phone} onChange={(e) => setEditing({ ...editing, phone: e.target.value })} /></div>
              <div><Label className="mb-1.5 block text-sm font-semibold">Ward</Label><Input data-testid="edit-ward" value={editing.ward} onChange={(e) => setEditing({ ...editing, ward: e.target.value })} /></div>
              <div><Label className="mb-1.5 block text-sm font-semibold">PVC Status</Label>
                <Select value={editing.pvc_status} onValueChange={(v) => setEditing({ ...editing, pvc_status: v })}>
                  <SelectTrigger data-testid="edit-pvc"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="has_pvc">Has PVC</SelectItem><SelectItem value="needs_transfer">Needs Transfer</SelectItem><SelectItem value="registering_new">New Reg</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
            <Button data-testid="edit-save" onClick={save} disabled={saving} className="bg-primary gap-2">{saving && <Loader2 className="w-4 h-4 animate-spin" />}Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
